import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { useShellDrawerContentRight } from "@/hooks/use-shell-drawer-content-right"
import { useWorkspaceEntityContentInset } from "@/hooks/use-workspace-entity-content-inset"
import { cn } from "@/lib/utils"

/** html attr — window-bar / drawer pointer-events while a fullscreen panel is open. */
export const WORKSPACE_FULLSCREEN_OPEN_ATTR = "data-workspace-fullscreen-open"

const openFullscreenIds = new Set<string>()

function syncFullscreenOpenAttr() {
  document.documentElement.toggleAttribute(WORKSPACE_FULLSCREEN_OPEN_ATTR, openFullscreenIds.size > 0)
}

export type WorkspaceFullscreenNested = boolean | 2 | 3

const CONTENT_Z: Record<"base" | "1" | "2" | "3", string> = {
  base: "z-[101]",
  "1": "z-[111]",
  "2": "z-[113]",
  "3": "z-[131]",
}

function contentZClass(nested: WorkspaceFullscreenNested | undefined): string {
  if (nested === 3) return CONTENT_Z["3"]
  if (nested === 2) return CONTENT_Z["2"]
  if (nested) return CONTENT_Z["1"]
  return CONTENT_Z.base
}

export type WorkspaceFullscreenPanelProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  /**
   * Raise above ShotPanel / other fullscreen hosts.
   * Matches WorkspaceDialog nested ladder (111 / 113 / 131).
   */
  nested?: WorkspaceFullscreenNested
  /**
   * Match entity-split content column width (leave agent column uncovered).
   * Ignored when immersive overrides use `contentClassName` with `!inset-0`.
   */
  fitEntityContent?: boolean
  /**
   * Fill the nearest positioned parent instead of a fixed viewport layer.
   * Shot 3D editor uses this so the panel moves with the column splitter.
   */
  contained?: boolean
  /** md+: start right of the shell sidebar rail instead of covering it (ShotPanel). */
  clearShellSidebar?: boolean
  /** Hide chrome header; keep title sr-only when `title` is set. */
  hideHeader?: boolean
  /** Left of title/description (e.g. QR). */
  headerLeading?: ReactNode
  title?: ReactNode
  description?: ReactNode
  descriptionClassName?: string
  closeAriaLabel?: string
  /** Extra controls before the close button (visible header only). */
  headerActions?: ReactNode
  /** End of header row before close (e.g. shot nav thumbs). */
  headerEnd?: ReactNode
  bodyClassName?: string
  contentClassName?: string
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  onOpenAutoFocus?: (event: Event) => void
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * Fullscreen workspace panel shell (ShotPanel / LocationScene3d).
 * Under window-bar on desktop; no overlay; modal={false} so shell chrome stays usable.
 */
export function WorkspaceFullscreenPanel({
  open,
  onOpenChange,
  children,
  nested = false,
  fitEntityContent = false,
  contained = false,
  clearShellSidebar = false,
  hideHeader = false,
  headerLeading,
  title,
  description,
  descriptionClassName,
  closeAriaLabel,
  headerActions,
  headerEnd,
  bodyClassName,
  contentClassName,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
}: WorkspaceFullscreenPanelProps) {
  const drawerRight = useShellDrawerContentRight()
  const entityInset = useWorkspaceEntityContentInset(!contained && fitEntityContent && open)
  const instanceId = useId()
  // Once the panel has lived in the column, keep that host as the portal container.
  // Switching the container (column → document.body) remounts the scene, so the phone
  // camera button opened the modal and the fresh mount closed it again.
  const [containedHost, setContainedHost] = useState<HTMLDivElement | null>(null)
  const hostLockedRef = useRef(false)
  const homeParentRef = useRef<HTMLElement | null>(null)
  if (contained) hostLockedRef.current = true
  const portalInHost = hostLockedRef.current
  const portalReady = !portalInHost || containedHost != null

  useEffect(() => {
    if (open) openFullscreenIds.add(instanceId)
    else openFullscreenIds.delete(instanceId)
    syncFullscreenOpenAttr()
    return () => {
      openFullscreenIds.delete(instanceId)
      syncFullscreenOpenAttr()
    }
  }, [open, instanceId])

  // Каждый коммит: React может вернуть хост в колонку. Пока модалка открыта, узел должен
  // оставаться на body выше меню оболочки. После закрытия — снова в слоте, не на весь экран.
  useLayoutEffect(() => {
    const host = containedHost
    if (!host || !hostLockedRef.current) return
    if (contained) {
      const home = homeParentRef.current
      if (home?.isConnected && host.parentElement !== home) home.appendChild(host)
      else if (!home && host.parentElement && host.parentElement !== document.body) {
        homeParentRef.current = host.parentElement
      }
      return
    }
    if (!homeParentRef.current && host.parentElement && host.parentElement !== document.body) {
      homeParentRef.current = host.parentElement
    }
    if (host.parentElement !== document.body) document.body.appendChild(host)
  })

  const contentStyle = contained
    ? undefined
    : fitEntityContent
      ? { left: entityInset.left, right: entityInset.right }
      : { right: drawerRight }

  const content = (
    <Dialog.Content
      {...(contained ? {} : { "data-desktop-below-header": "" })}
      {...(clearShellSidebar && !contained ? { "data-clear-shell-sidebar": "" } : {})}
      className={cn(
        "ui-dialog-fade flex flex-col bg-ui-bg-elevated focus:outline-none",
        contained ? "absolute inset-0 z-50 h-full w-full" : "fixed inset-0 h-dvh",
        !contained && !fitEntityContent && "transition-[left,right] duration-300 ease-out",
        !contained && contentZClass(nested),
        contentClassName,
      )}
      style={contentStyle}
      onOpenAutoFocus={(e) => {
        if (onOpenAutoFocus) onOpenAutoFocus(e)
        else e.preventDefault()
      }}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={onEscapeKeyDown}
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
    >
      {hideHeader ? (
        <>
          {title != null ? <Dialog.Title className="sr-only">{title}</Dialog.Title> : null}
          {description != null ? (
            <Dialog.Description className="sr-only">{description}</Dialog.Description>
          ) : title != null ? (
            <Dialog.Description className="sr-only">
              {typeof title === "string" ? title : closeAriaLabel}
            </Dialog.Description>
          ) : null}
        </>
      ) : (
        <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-2">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="min-w-0 overflow-hidden">
              {title != null ? (
                <Dialog.Title className="truncate text-base font-semibold text-ui-text-primary">{title}</Dialog.Title>
              ) : null}
              {description != null ? (
                <Dialog.Description className={cn("truncate text-xs text-ui-text-muted", descriptionClassName)}>
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">
                  {typeof title === "string" ? title : (closeAriaLabel ?? "")}
                </Dialog.Description>
              )}
            </div>
            {headerLeading ? <div className="shrink-0 self-center">{headerLeading}</div> : null}
          </div>
          {headerEnd}
          {headerActions}
          {closeAriaLabel ? (
            <Dialog.Close
              type="button"
              className="shrink-0 rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
              aria-label={closeAriaLabel}
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          ) : null}
        </div>
      )}
      <div className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col", bodyClassName)}>{children}</div>
    </Dialog.Content>
  )

  return (
    <Dialog.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <div
        ref={setContainedHost}
        className={cn(
          !portalInHost && "hidden",
          portalInHost && contained && "absolute inset-0",
          portalInHost && !contained && "fixed inset-0",
          portalInHost && !contained && contentZClass(nested),
        )}
      />
      {portalReady ? (
        <Dialog.Portal container={portalInHost ? (containedHost ?? undefined) : undefined}>{content}</Dialog.Portal>
      ) : null}
    </Dialog.Root>
  )
}
